import { ErrorBoundary } from "@/components/error-boundary";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useUrlFilters } from "@/hooks/use-url-filters";
import { useSearchPageMapData } from "@/providers/search";
import { hasProjectsWithSUPs } from "@/utils/map-helpers";
import { LoaderCircleIcon } from "lucide-react";
import React, { useMemo } from "react";
import { ListSkeleton } from "../list/skeleton";
import { MatchingProjectsList } from "./matching-projects-list";
import { NonMatchingProjectsList } from "./non-matching-projects-list";

interface Params {
	hideType: string;
	milestones: { id: string; type: string; milestone: string }[];
}

export const getMilestones = ({ milestones, hideType }: Params): { id: string; type: string }[] | undefined => {
	if (hideType === "milestones") return undefined;
	if (hideType === "rejected") milestones = milestones.filter(x => x.milestone !== "rejected");
	return milestones.map(m => ({ id: m.id, type: m.type }));
};

interface ProjectListProps {
	className?: string;
	style?: React.CSSProperties;
}

const ListViewFallback = ({ children }: { children?: React.ReactNode }) => (
	<div className="w-full h-full flex items-center justify-center bg-gray-50 relative">
		<div className="text-center p-6">
			<div className="text-gray-800 font-semibold mb-2">List View Error</div>
			<p className="text-gray-600 text-sm">Unable to load project list. Try refreshing the page.</p>
		</div>
		{children}
	</div>
);

export const ProjectList = React.memo(function ProjectList({ className, style, ...props }: ProjectListProps) {
	const projectData = useSearchPageMapData(state => state.nearByprojects);
	const isMapDataFetching = useSearchPageMapData(state => state.isMapDataFetching);
	const isNearByProjectDataFetching = useSearchPageMapData(state => state.isNearByProjectDataFetching);
	const isInitialBoundsFetch = useSearchPageMapData(state => state.isInitialBoundsFetch);
	const {
		filters: { selectedLocationId, locations },
	} = useUrlFilters();

	// Combined loading state - show loading when either map data or nearby projects are being fetched
	const isMapNearByProjectsFetching = isMapDataFetching || isNearByProjectDataFetching;

	const matchingProjectData = useMemo(
		() => projectData.filter(({ isExactMatch }) => Boolean(isExactMatch)),
		[projectData],
	);
	const nonMatchingProjectData = useMemo(
		() => projectData.filter(({ isExactMatch }) => !Boolean(isExactMatch)),
		[projectData],
	);

	// Check if projects have any actual properties (SUPs)
	const hasMatchingPropertiesWithSUPs = useMemo(() => hasProjectsWithSUPs(matchingProjectData), [matchingProjectData]);
	const hasNonMatchingPropertiesWithSUPs = useMemo(
		() => hasProjectsWithSUPs(nonMatchingProjectData),
		[nonMatchingProjectData],
	);

	const [isMounted, setIsMounted] = React.useState(false);
	const isXL = useMediaQuery("(min-width: 1280px)");
	const columnsCount = isXL ? 2 : 1;

	React.useEffect(() => {
		setIsMounted(true);
	}, []);

	const locationName = React.useMemo(() => {
		// Only show project name when a specific project is selected by the user
		// Don't show anything for custom map searches or when no specific location is selected

		// If this is a custom map search, don't show any location name
		if (selectedLocationId === "custom") {
			return null;
		}

		// First priority: Try to find the location that matches selectedLocationId by ID
		if (selectedLocationId && locations?.length) {
			const selectedLocation = locations.find(
				(location: { id: string; name: string; type: string }) => location.id === selectedLocationId,
			);
			// Only return name if it's a project type
			if (selectedLocation?.name && selectedLocation.type === "project") {
				return selectedLocation.name;
			}
		}

		// Fallback: If selectedLocationId exists but didn't match (e.g., ID not enriched yet),
		// check if there's a single location of type "project" in the locations array
		if (selectedLocationId && locations?.length === 1) {
			const firstLocation = locations[0];
			if (firstLocation?.name && firstLocation.type === "project") {
				return firstLocation.name;
			}
		}

		// Second priority: Try to find the project that matches selectedLocationId in projectData
		if (selectedLocationId && projectData.length > 0) {
			const selectedProject = projectData.find(project => project.projectId === selectedLocationId);
			if (selectedProject?.projectName) {
				return selectedProject.projectName;
			}
		}

		// If no specific location is selected, don't show a location name
		// (Previously this would pick the first project, which was incorrect)
		return null;
	}, [selectedLocationId, projectData, locations]);

	const hasNonMatchingProjects = nonMatchingProjectData.length > 0;
	// Check if we have any properties (SUPs) in the projects, not just projects themselves
	const hasAnyProjects = hasMatchingPropertiesWithSUPs || hasNonMatchingPropertiesWithSUPs;

	// Check if the selected location has 0 properties
	const selectedLocationPropertyCount = React.useMemo(() => {
		if (!selectedLocationId) return -1; // -1 means no location selected

		// Use stable projectId for matching instead of unreliable string comparison
		return projectData
			.filter(project => project.projectId === selectedLocationId)
			.reduce((total, project) => total + (project.sups?.length || 0), 0);
	}, [projectData, selectedLocationId]);

	// Show banner when selected location has 0 properties but there are other properties to show
	const shouldShowBanner = locationName && selectedLocationPropertyCount === 0 && hasAnyProjects;

	return (
		<ErrorBoundary fallback={ListViewFallback}>
			<div className={`w-full ${className}`} style={style} {...props}>
				{isMapNearByProjectsFetching ? (
					<div className="px-2 pt-2">
						<ListSkeleton count={6} />
					</div>
				) : !isMounted ? (
					<div className="flex flex-col w-full relative h-full overflow-y-auto">
						<div className="px-2 pt-2">
							<ListSkeleton count={6} />
						</div>
					</div>
				) : (
					<div
						className="flex flex-col w-full relative h-full"
						style={{
							height: "100%",
							overflowY: "auto",
							contain: "strict",
						}}
					>
						{hasAnyProjects ? (
							<div className="w-full h-full overflow-y-auto">
								{/* Show informative message when selected location has 0 properties but nearby properties exist */}
								{shouldShowBanner && (
									<div className="px-4 py-3 bg-blue-50 border-l-4 border-blue-400">
										<p className="text-sm text-gray-700">
											<span className="font-semibold text-gray-900">0 properties in {locationName}</span>
											<span className="block mt-1">Showing properties in and around {locationName}</span>
										</p>
									</div>
								)}

								{/* Show matching properties section */}
								<MatchingProjectsList
									projectData={matchingProjectData}
									locationName={shouldShowBanner ? null : locationName}
									columnsCount={columnsCount}
									locationPropertyCount={selectedLocationPropertyCount}
								/>

								{/* Show nearby properties section */}
								{hasNonMatchingProjects && (
									<NonMatchingProjectsList projectData={nonMatchingProjectData} columnsCount={columnsCount} />
								)}
							</div>
						) : (
							<div className="flex flex-col items-center justify-center h-full px-4">
								<div className="text-center space-y-4 max-w-md mx-auto">
									<div className="text-6xl mb-4">🏠</div>
									<h3 className="text-xl font-semibold text-gray-900">No properties found</h3>
									<p className="text-gray-600">
										No properties match your current search criteria. Try adjusting your filters to see more results.
									</p>
									<div className="mt-6 p-4 bg-gray-50 rounded-lg">
										<p className="text-sm font-medium text-gray-700 mb-3">Suggestions:</p>
										<ul className="text-sm text-gray-600 space-y-2 text-left">
											<li className="flex items-start">
												<span className="text-gray-400 mr-2">•</span>
												<span>Expand your location search radius</span>
											</li>
											<li className="flex items-start">
												<span className="text-gray-400 mr-2">•</span>
												<span>Adjust your budget range</span>
											</li>
											<li className="flex items-start">
												<span className="text-gray-400 mr-2">•</span>
												<span>Remove some active filters</span>
											</li>
											<li className="flex items-start">
												<span className="text-gray-400 mr-2">•</span>
												<span>Try different property types</span>
											</li>
										</ul>
									</div>
								</div>
							</div>
						)}

						{isMapNearByProjectsFetching && isInitialBoundsFetch && hasAnyProjects && (
							<div className="px-4 py-3 bg-white border-t border-gray-200">
								<div className="flex items-center justify-center">
									<LoaderCircleIcon className="animate-spin w-4 h-4 text-blue-600" />
									<span className="ml-2 text-sm text-gray-600">Loading more properties...</span>
								</div>
							</div>
						)}

						{isMapNearByProjectsFetching && !isInitialBoundsFetch && hasAnyProjects && (
							<div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-20 flex items-start justify-center pt-4">
								<div className="flex items-center bg-white px-4 py-2 rounded-lg shadow-md">
									<LoaderCircleIcon className="animate-spin w-5 h-5 text-blue-600" />
									<span className="ml-2 text-sm font-medium text-gray-700">Loading properties...</span>
								</div>
							</div>
						)}
					</div>
				)}
			</div>
		</ErrorBoundary>
	);
});
